<!-- =========================================================================================
    File Name: Bills.vue
    Description: Displays the list of user's company bills
========================================================================================== -->

<template>
    <div class="edit-bill">
        <column-layout>
            <div class="flex flex-wrap justify-between items-start mb-8">
                <h2 class="font-light text-lg my-3">EDITER UNE FACTURE</h2>
                <div class="flex">
                    <vs-button :to="{path: '/factures' }" type="gradient" icon-pack="feather" icon="icon-chevron-left"
                               class="mr-2">
                        Retour
                    </vs-button>
                </div>
            </div>

            <vs-card class="text-left px-0" v-if="bill">
                <!-- bill summary line -->
                <div class="mx-4 flex justify-between items-center">
                    <div class="flex items-center">
                        <div class="w-12 h-12 pt-1 text-xl items-center justify-center inline-flex rounded-full"
                             v-if="bill.status"
                             :class="`text-${ currentBillStatus(bill.status.code).color }`"
                             :style="{background: `rgba(var(--vs-${ currentBillStatus(bill.status.code).color }),.15)`}">
                            <i class="m-auto leading-normal fal fa-lg fa-fw fa-file-invoice"/>
                        </div>
                        <div class="ml-4">
                            <strong>Facture</strong><br>
                            <span class="text-gray-dark">N° {{ bill.number }}</span>
                        </div>
                    </div>
                    <div class="flex items-center" v-if="bill.status">
                        <strong class="block mr-2 leading-none">Statut actuel&nbsp;:</strong>
                        <vs-chip :color="currentBillStatus(bill.status.code).color" style="white-space: nowrap;">
                            <strong>{{ currentBillStatus(bill.status.code).status }}</strong>
                        </vs-chip>
                    </div>
                </div>

                <vs-divider class="my-2 card-wide"/>

                <!-- Bills details -->
                <section class="vx-row pl-4 items-start">
                    <div class="vx-col hidden lg:block lg:w-1/5">
                        <div class="bill-section-icon"></div>
                    </div>
                    <div class="vx-col w-full lg:w-4/5 text-base pl-4 pr-8 py-3">
                        <div class="vx-row items-center">
                            <div class="vx-col w-full sm:w-6/12 mb-3 text-2xl">
                                <span class="text-dark">Facture N°&nbsp;:</span>&nbsp;<span
                                class="text-gray-dark">{{ bill.number }}</span>
                            </div>
                        </div>
                        <div class="vx-row items-center">
                            <div class="vx-col w-full sm:w-6/12 mb-2">
                                <span class="text-dark"><i class="fal fa-calendar mr-2"></i>Émise&nbsp;le&nbsp;:</span>&nbsp;<span
                                class="text-gray-dark">
                  {{ bill.emission_date | moment('DD/MM/YYYY') }}
              </span>
                            </div>
                            <div class="vx-col w-full sm:w-6/12 mb-2 sm:text-right">
                                <span class="text-dark"><i
                                    class="fal fa-calendar mr-2"></i>Échéance&nbsp;le&nbsp;:</span>&nbsp;<span
                                :class="{'text-primary': !isLate, 'text-danger': isLate }">{{
                                    bill.due_date | moment('DD/MM/YYYY')
                                }}</span>
                            </div>
                        </div>

                        <div class="vx-row items-center">
                            <div class="vx-col w-full sm:w-6/12 mb-2">
                                <span class="text-dark"><i
                                    class="fal fa-receipt mr-2"></i>Montant&nbsp;:</span>&nbsp;<span
                                class="text-success">{{ bill.amount_ttc | numFormat('$ 0,0.00') }}</span>
                            </div>
                            <div class="vx-col w-full sm:w-6/12 mb-2 sm:text-right">
                                <span class="text-dark"><i
                                    class="fal fa-badge-dollar mr-2"></i>Montant réglé&nbsp;:</span>&nbsp;
                                <span
                                    :class="{ 'text-danger': bill.total_paid < bill.amount_ttc, 'text-primary': bill.total_paid >= bill.amount_ttc }">{{
                                        bill.total_paid | numFormat('$ 0,0.00')
                                    }}</span>
                            </div>
                        </div>

                        <div class="vx-row items-center mt-2">
                            <div class="vx-col w-full sm:w-6/12 mb-2">
                                <span class="text-dark">Destinataire&nbsp;:</span>&nbsp;
                                <span class="text-gray-dark">
                      <template v-if="bill.destinataire">
                      {{ bill.destinataire.name }}
                      </template>
                        <template v-else>
                            <span class="text-danger">Aucun destinataire</span>
                        </template>
                  </span>
                            </div>
                            <div class="vx-col w-full sm:w-6/12 mb-2 sm:text-right">
                                <vs-button v-if="bill.destinataire"
                                           :to="{ name: 'Editer un client', params: { id: bill.destinataire.id } }"
                                           color="primary" type="border">Afficher le client
                                </vs-button>
                            </div>
                        </div>
                    </div>
                </section>

                <vs-divider class="mt-0 mb-2 card-wide"/>

                <vs-tabs ref="tabs">
                    <vs-tab label="Activité">
                        <div class="tab-text pt-6">

                            <component :is="scrollbarTag" ref="historyLog" class="scroll-area" :settings="settings"
                                       :key="$vs.rtl">
                                <activity-timeline :data="historyData"/>
                            </component>

                        </div>
                    </vs-tab>
                    <vs-tab label="Règlements">
                        <div class="tab-text pt-6">

                            <component :is="scrollbarTag" ref="paimentLog" class="scroll-area" :settings="settings"
                                       :key="$vs.rtl">
                                <vx-timeline :data="paiementData"/>
                            </component>

                        </div>
                    </vs-tab>
                    <vs-tab label="Commentaires">
                        <div class="tab-text pt-6">

                            <component :is="scrollbarTag" ref="commentsLog" class="scroll-area" :settings="settings"
                                       :key="$vs.rtl">
                                <vx-timeline :data="commentsData"/>
                            </component>

                        </div>
                    </vs-tab>
                </vs-tabs>

                <div slot="footer">
                    <vs-row vs-justify="flex-end" class="pb-24 sm:pb-16 px-4">
                        <vs-button @click="activeModalComment = true" size="large"
                                   class="leading-none px-6 py-3 text-sm ml-2 mb-2 sm:mb-0" icon-pack="far"
                                   icon="fa-comment mr-3" type="border" color="primary">Ajouter<br>un commentaire
                        </vs-button>
                        <vs-button @click="activeModalPaiement = true" size="large"
                                   class="leading-none px-6 py-3 text-sm ml-2 mb-2 sm:mb-0" icon-pack="far"
                                   icon="fa-wallet mr-3" type="gradient" color="primary">Enregistrer<br>paiement(s)
                        </vs-button>
                    </vs-row>
                </div>
            </vs-card>
            <empty-card v-else/>

            <!--  ASIDE -->
            <template slot="aside">

                <h2 class="font-light mt-10 text-lg mb-4">Prévisualisation :</h2>
                <vs-card class="px-4 text-center">
                    <div class="bill-preview">
                        <!-- <img v-if="bill.visual" :src="'data:image/jpeg;base64,' + bill.visual" :alt="`Prévisualisation de la facture #${bill.num}`"> -->
                        <div v-if="bill.visual && bill.visual.length">
                            <div v-for="(img, idx) in bill.visual" :key="idx" class="mb-4">
                                <VuePureLightbox
                                    :thumbnail="'data:image/png;base64,' + img"
                                    :images="['data:image/png;base64,' + img]"
                                />
                            </div>
                            <p class="text-gray-dark text-sm"><i class="far fa-search mr-2"/>Cliquez sur l’image pour agrandir.</p>
                        </div>
                        <div v-else class="bill-sample flex flex-row items-end justify-center">
                            <div class="text-gray text-lg mb-10">Aucun document attaché à cette facture.</div>
                        </div>
                    </div>
                </vs-card>
            </template>

        </column-layout>


        <vs-prompt
            title="Enregistrer un nouveau règlement"
            @cancel="clearPaiementForm"
            @accept="acceptPaiementForm"
            :is-valid="validPaiement"
            accept-text="Envoyer"
            cancel-text="Annuler"
            :active.sync="activeModalPaiement">
            <div class="payment-form">
                <i class="far fa-info-circle mr-2"></i>Veuillez saisir les informations ci-dessous :

                <vs-input
                    type="number"
                    label="Montant"
                    v-model.number="formPaiement.montant"
                    class="mt-4 mb-2 w-full"
                    :danger="!Boolean(formPaiement.montant)"
                    danger-text="Vous devez indiquer un montant"
                    placeholder="montant en €"
                    val-icon-pack="feather"
                    val-icon-danger="icon-x"
                />

                <div class="vs-component vs-con-input-label vs-input mt-4 mb-2 w-full vs-input-primary">
                    <label class="vs-input--label" for="dateReglement"><i class="far fa-calendar mr-1"></i> Date du
                        règlement</label>
                    <datepicker monday-first :language="fr" v-model="formPaiement.date" :value="formPaiement.date"
                                id="dateReglement" class="mt-1" :disabled-dates="{from: new Date() }"
                                placeholder="Sélectionnez une date" format="dd/MM/yyyy"></datepicker>
                </div>

                <vs-alert :active="!Boolean(formPaiement.date)" color="danger">
                    Vous devez indiquer une date de règlement
                </vs-alert>

            </div>
        </vs-prompt>

        <vs-prompt
            title="Editer un commentaire"
            @cancel="clearCommentForm"
            @accept="acceptCommentForm"
            :is-valid="validComment"
            accept-text="Enregistrer"
            cancel-text="Annuler"
            :active.sync="activeModalComment">
            <div class="comment-form">
                <i class="far fa-info-circle mr-2"></i>Veuillez éditer votre commentaire ci-dessous :
                <vs-textarea
                    label="Votre commentaire"
                    id="billComment"
                    rows="4"
                    class="mt-4 mb-2 w-full"
                    placeholder=""
                    v-model="formComment.comment"
                    :danger="!Boolean(formComment.comment)"
                    danger-text="Vous devez écrire un commentaire"
                    height="200px"/>
            </div>
        </vs-prompt>

        <!-- <pre>
          {{ bill }}
        </pre> -->

    </div>
</template>

<script>
// @ is an alias to /src
import ColumnLayout from '@/layouts/columns/ColumnLayout.vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import ActivityTimeline from '@/components/timeline/ActivityTimeline'
import VxTimeline from '@/components/timeline/VxTimeline'
import {fr} from 'vuejs-datepicker/dist/locale'
import Datepicker from 'vuejs-datepicker'
import billMixins from '@/mixins/billMixins'
import VuePureLightbox from 'vue-pure-lightbox'

import numeral from 'numeral'

import {mapState, mapActions} from 'vuex'

export default {
    name: 'EditBill',
    mixins: [billMixins],
    components: {
        VuePureLightbox,
        ColumnLayout,
        ActivityTimeline,
        VxTimeline,
        VuePerfectScrollbar,
        Datepicker
    },

    props: {
        id: {
            type: [Number, String],
            required: true
        }
    },

    data() {
        return {
            fr,
            activeModalPaiement: false,
            activeModalComment: false,
            formPaiement: {
                montant: '',
                date: ''
            },

            formComment: {
                comment: '',
                date: ''
            },

            settings: { // perfectscrollbar settings
                maxScrollbarLength: 60,
                wheelSpeed: .60
            }
        }
    },

    computed: {
        ...mapState({
            bill: state => state.bill.bill,
            billLoading: state => state.bill.billLoading
        }),

        scrollbarTag() {
            return this.$store.getters.scrollbarTag
        },

        historyData() {
            //todo avec les logs
            const historyData = [];

            const today = this.$moment()
            if (this.bill && this.bill.actions && this.bill.actions.length) {
                for (const item of this.bill.actions) {
                    const d = this.$moment(item.action_date || new Date())
                    const diff = today.diff(d, 'days')

                    let itemOptionStatus = this.billStatusOptions.find(o => o.code === item.type)
                    if (!itemOptionStatus) itemOptionStatus = {color: 'primary', icon: 'InfoIcon'}

                    historyData.push({
                        color: itemOptionStatus.color,
                        icon: itemOptionStatus.icon,
                        title: item.last_action,
                        desc: item.resume,
                        time: (diff < 22) ? d.fromNow() : `le ${d.format('DD/MM/YYYY')}`,
                        contact: ''
                    })
                }
            } else {
                historyData.push({
                    color: 'info',
                    icon: 'InfoIcon',
                    title: 'Aucune activité',
                    desc: '-',
                    time: '',
                    contact: ''
                })
            }
            return historyData
        },

        paiementData() {
            const paiementData = []

            if (this.bill.historiqueReglements && this.bill.historiqueReglements.length) {
                for (const item of this.bill.historiqueReglements) {
                    const d = this.$moment(item.date || new Date())

                    paiementData.push({
                        color: 'success',
                        icon: 'CreditCardIcon',
                        title: 'Nouveau paiement',
                        desc: `Montant enregistré :  € ${numeral(item.montant).format('1,000.00')}`,
                        time: `le ${d.format('DD/MM/YYYY')}`
                    })
                }
            } else {
                paiementData.push({
                    color: 'info',
                    icon: 'InfoIcon',
                    title: 'Aucun règlement enregistré pour l’instant',
                    desc: '-',
                    time: ''
                })
            }

            return paiementData
        },

        commentsData() {
            const commentsData = []

            if (this.bill.historiqueCommentaires && this.bill.historiqueCommentaires.length) {
                for (const item of this.bill.historiqueCommentaires) {
                    const d = this.$moment(item.date || new Date())

                    commentsData.push({
                        color: 'info',
                        icon: 'MessageSquareIcon',
                        title: 'Nouveau commentaire',
                        desc: item.comment,
                        time: `le ${d.format('DD/MM/YYYY')}`
                    })
                }
            } else {
                commentsData.push({
                    color: 'info',
                    icon: 'MessageSquareIcon',
                    title: 'Aucun commentaire ajouté pour l’instant',
                    desc: '-',
                    time: ''
                })
            }

            return commentsData
        },

        validPaiement() {
            return Boolean(this.formPaiement.montant) && this.formPaiement.date !== ''
        },

        validComment() {
            return Boolean(this.formComment.comment)
        },

        isLate() {
            const emission = this.$moment(this.bill.due_date)
            const echeance = this.$moment(this.bill.due_date)
            const diff = echeance.diff(emission, 'days')
            // console.log(diff)
            return (diff < 0)
        }
    },

    created() {
        this.refreshData()
    },

    methods: {
        ...mapActions('bill', ['getBill', 'addCommentToBill', 'addReglementToBill', 'deleteBill']),

        refreshData() {
            this.$vs.loading()
            this.getBill(this.id).then(() => {
                this.$vs.loading.close()
            }).catch(() => {
                this.$vs.loading.close()
            })
        },

        deleteItem() {
            // console.log('deletion not permited')
            // this.deleteBill(this.id).then(() => {
            //   this.$router.push({path: '/factures'})
            // })
        },


        // paiement form
        clearPaiementForm() {
            this.formPaiement.montant = ''
            this.formPaiement.date = ''

        },

        acceptPaiementForm() {
            this.addReglementToBill({
                id: this.bill.id,
                reglement:
                    {
                        date: this.formPaiement.date,
                        amount: this.formPaiement.montant
                    }
            }).then(() => {
                this.getBill(this.id).then(() => {
                    this.$refs.tabs.value = 1
                    this.formPaiement.montant = ''
                    this.formPaiement.date = ''
                })
            })
        },

        // comment form
        clearCommentForm() {
            this.formComment.comment = ''
            this.formComment.date = ''

        },

        acceptCommentForm() {
            this.addCommentToBill({id: this.bill.id, comment: this.formComment.comment})
                .then(() => {
                    this.getBill(this.id).then(() => {
                        this.$refs.tabs.value = 2
                        this.formComment.comment = ''
                        this.formComment.date = ''
                    })
                })
        }
    }
}
</script>

<style lang="scss">
.bill-section-icon {
    background: url('~@/assets/images/icons/section-bill-icon.svg') no-repeat right center;
    height: 100%;
    min-height: 178px;
    width: calc(100% + 2.15rem);
    max-width: 170px;
    margin: -.4rem 0 0 -1.6rem;
}

.card-wide {
    margin-left: -0.6rem;
    margin-right: -0.6rem;
    width: calc(100% + 1.2rem);
}

.history-section {
    height: 340px;
}


.bill-sample {
    background: url('~@/assets/images/icons/circle-bill-icon.svg') no-repeat 50% 40px;
    width: 100%;
    max-width: 200px;
    height: 340px;
    background-size: contain;
    padding: 104px 0 0;
    margin: auto;
}

.bill-preview {
    img {
        max-width: 100%;
        width: 100%;
        height: auto;
    }
}
</style>
